import { keyframes } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { memo, useEffect, useState } from 'react';

const spin = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const driving = keyframes`
  0% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-2px);
  }
  50% {
    transform: translateY(2px);
  }
  75% {
    transform: translateY(-1px);
  }
  100% {
    transform: translateY(0);
  }
`;

const circleSx = {
  animation: `${spin} 2s linear infinite`,
  transformOrigin: 'center center',
};

const vanSx = {
  animation: `${driving} 2s linear infinite`,
  transformOrigin: 'center center',
};

/**
 * @param {boolean} hasLabel
 * @returns {Object}
 */

const boxStyle = (hasLabel) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: hasLabel ? 2 : 0,
});

/**
 * @param {object} props - Component props
 * @param {boolean} [props.isLoading=true] - Loading state
 * @param {number} [props.size=100] - Size of the logo
 * @param {string | null} [props.label=null] - Label to display
 * @returns {React.JSX.Element}
 */

const AnimatedLogoComponent = ({ isLoading = true, size = 100, label = null }) => {
  const [isAnimated, setIsAnimated] = useState(isLoading);

  useEffect(() => {
    setIsAnimated(isLoading);
  }, [isLoading]);

  const icon = (
    <svg
      height={size}
      id="Logo"
      viewBox="0 0 220 220"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <Box component="g" sx={isAnimated ? circleSx : undefined}>
        <circle cx="110" cy="110" fill="#eac435" r="110" />
        <path
          d="M245,149.56h-4.75c0,.14,0,.28,0,.42a90.25,90.25,0,1,1-90.67-90.25V55A95,95,0,1,0,245,150C245,149.83,245,149.7,245,149.56Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
      </Box>
      <Box component="g" sx={isAnimated ? vanSx : undefined}>
        <path
          d="M191.07,173.87"
          fill="#eac435"
          stroke="#0e3b43"
          strokeMiterlimit="10"
          strokeWidth="2.32px"
          transform="translate(-40 -40)"
        />
        <path
          d="M91.24,125.49,171.92,95c1.51-.57,3.36.17,3.36,1.35v30.45c0,.85-1,1.54-2.26,1.54H92.34C90,128.37,89.2,126.26,91.24,125.49Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
        <path
          d="M218.71,166.33,212.94,183a1.49,1.49,0,0,1-1.41,1H90.07a1.49,1.49,0,0,1-1.37-.89l-7.31-16.66a1.64,1.64,0,0,1-.1-1.07l7.5-32.48a1.68,1.68,0,0,1,1.64-1.3h116a1.66,1.66,0,0,1,1.58,1.14l10.81,32.74A1.87,1.87,0,0,1,218.71,166.33Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
        <path
          d="M211.57,186.32c1.17,0,2,.84,1.56,1.63l-1.43,2.67a1.92,1.92,0,0,1-1.72.83H90.83a2,2,0,0,1-1.73-.83L87.67,188c-.42-.79.39-1.63,1.57-1.63Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
        <g>
          <circle
            cx="146.19"
            cy="147.63"
            fill="#0e3b43"
            r="15.79"
            stroke="#eac435"
            strokeMiterlimit="10"
            strokeWidth="2.32px"
          />
          <circle
            cx="73.71"
            cy="147.63"
            fill="#0e3b43"
            r="15.79"
            stroke="#eac435"
            strokeMiterlimit="10"
            strokeWidth="2.32px"
          />
        </g>
        <circle cx="73.71" cy="147.62" fill="#eac435" r="7.48" />
        <circle cx="146.18" cy="147.62" fill="#eac435" r="7.48" />
        <path
          d="M203.89,128.37H182.07a1.71,1.71,0,0,1-1.8-1.6v-4a1.71,1.71,0,0,1,1.8-1.61h18.82a1.88,1.88,0,0,1,1.5.72l3,4A1.6,1.6,0,0,1,203.89,128.37Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
        <path
          d="M180.12,138.39V154.1a1.49,1.49,0,0,0,1.52,1.45h25.87A1.49,1.49,0,0,0,209,154.1v-1.21a1.44,1.44,0,0,0-.08-.46l-5-14.5a1.51,1.51,0,0,0-1.44-1H181.64A1.49,1.49,0,0,0,180.12,138.39Z"
          fill="#eac435"
          transform="translate(-40 -40)"
        />
        <path
          d="M92.45,155.55h18.11A1.45,1.45,0,0,0,112,154.1V138.39a1.45,1.45,0,0,0-1.45-1.46H96.07a1.56,1.56,0,0,0-1.51,1.2l-3.62,15.52A1.55,1.55,0,0,0,92.45,155.55Z"
          fill="#eac435"
          transform="translate(-40 -40)"
        />
        <rect fill="#eac435" height="18.62" rx="1.55" width="55.78" x="79.51" y="96.93" />
        <path
          d="M86.8,119l84-31.91a1,1,0,0,1,1.31.59l1,2.67a1,1,0,0,1-.59,1.31L88.54,123.58a1,1,0,0,1-1.31-.59l-1-2.67A1,1,0,0,1,86.8,119Z"
          fill="#0e3b43"
          transform="translate(-40 -40)"
        />
      </Box>
    </svg>
  );

  const text = (
    <Typography sx={{ whiteSpace: 'break-spaces', textAlign: 'center' }}>{label}</Typography>
  );

  return (
    <Box sx={boxStyle(label)}>
      {icon}
      {text}
    </Box>
  );
};

AnimatedLogoComponent.displayName = 'AnimatedLogo';

const AnimatedLogo = memo(AnimatedLogoComponent);

export default AnimatedLogo;
